import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Card, Field, Select } from '../ui';
import { api, ApiError } from '../../lib/api';
import type { Finding, Formulation } from '../../lib/types';
import { CheckPanel } from './CheckPanel';
import { SupplementFactsBox, type SfpBoxData } from './SupplementFactsBox';

type TargetPop = 'adults_children_4plus' | 'infants' | 'children_1_3' | 'pregnant_lactating';

export function SupplementFactsTab({
  orgId,
  pid,
  canVerify,
  canOverride,
  canAccept,
}: {
  orgId: string;
  pid: string;
  canVerify: boolean;
  canOverride: boolean;
  canAccept: boolean;
}) {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<Formulation[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [population, setPopulation] = useState<TargetPop>('adults_children_4plus');
  const [sfpData, setSfpData] = useState<SfpBoxData | null>(null);
  const [rules, setRules] = useState<{ items: Finding[]; valid: boolean } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const base = `/api/v1/orgs/${orgId}/products/${pid}`;

  // Load versions
  useEffect(() => {
    void api<{ formulations: Formulation[] }>(`${base}/formulations`)
      .then((r) => {
        setVersions(r.formulations);
        if (r.formulations[0]) {
          setSelectedVersion(r.formulations[0].version);
        }
      })
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)));
  }, [base]);

  // Load SFP data
  const loadSfp = useCallback(async () => {
    if (!selectedVersion) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await api<{ sfp: SfpBoxData; rules: { items: Finding[]; valid: boolean } }>(
        `${base}/sfp?version=${selectedVersion}&population=${population}`,
      );
      setSfpData(r.sfp);
      setRules(r.rules);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [base, population, selectedVersion]);

  useEffect(() => {
    void loadSfp();
  }, [loadSfp]);

  if (versions.length === 0) {
    return (
      <Card>
        <p className="text-sm text-slate-500">
          {t(
            'sfp.noFormulation',
            'No formulation versions available. Create a formulation in the Formulation tab first.',
          )}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {err && <Alert tone="red">{err}</Alert>}

      {/* Control bar */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Field label={t('sfp.version', 'Formulation Version')}>
              <Select
                value={String(selectedVersion ?? '')}
                onChange={(e) => setSelectedVersion(Number(e.target.value))}
                className="w-36"
              >
                {versions.map((v) => (
                  <option key={v.version} value={v.version}>
                    v{v.version} ({v.ingredients.length} ings)
                  </option>
                ))}
              </Select>
            </Field>

            <Field label={t('sfp.targetPopulation', 'Target Population')}>
              <Select
                value={population}
                onChange={(e) => setPopulation(e.target.value as TargetPop)}
                className="w-56"
              >
                <option value="adults_children_4plus">
                  {t('sfp.popAdults', 'Adults & Children >= 4 years')}
                </option>
                <option value="children_1_3">{t('sfp.popChildren', 'Children 1 through 3 years')}</option>
                <option value="pregnant_lactating">
                  {t('sfp.popPregnant', 'Pregnant & Lactating Women')}
                </option>
                <option value="infants">{t('sfp.popInfants', 'Infants through 12 months')}</option>
              </Select>
            </Field>
          </div>

          <div className="flex items-center gap-2">
            {rules && (
              <Badge tone={rules.valid ? 'green' : 'amber'}>
                {rules.valid
                  ? t('sfp.statusPass', '21 CFR 101.36 Format Compliant')
                  : t('sfp.statusReview', 'Format Review Needed')}
              </Badge>
            )}
          </div>
        </div>
      </Card>

      {/* Main content grid */}
      <div className="grid gap-6 lg:grid-cols-12">
        {/* Left column: Visual Supplement Facts Box */}
        <div className="lg:col-span-5">
          <Card title={t('sfp.panelPreview', 'Supplement Facts Panel (21 CFR 101.36)')}>
            {loading && <p className="py-6 text-center text-sm text-slate-500">{t('app.loading')}</p>}
            {!loading && sfpData && <SupplementFactsBox data={sfpData} />}
          </Card>
        </div>

        {/* Right column: Format Checklist & AI Verification */}
        <div className="space-y-6 lg:col-span-7">
          {/* Deterministic format checklist */}
          <Card title={t('sfp.formatChecklist', '21 CFR 101.36 Automated Format Checklist')}>
            {rules && (
              <div className="divide-y divide-slate-100 text-xs">
                {rules.items.map((it) => (
                  <div key={it.id} className="flex items-start justify-between py-2 gap-2">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5 font-medium text-slate-900">
                        <span>{it.subject}</span>
                        <span className="font-mono text-[10px] text-slate-400">({it.citation.section})</span>
                      </div>
                      <p className="text-slate-600">{it.finding}</p>
                    </div>
                    <Badge
                      tone={
                        it.severity === 'ok'
                          ? 'green'
                          : it.severity === 'critical'
                            ? 'red'
                            : it.severity === 'major'
                              ? 'amber'
                              : 'blue'
                      }
                    >
                      {it.severity.toUpperCase()}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* AI Check & Official Report Generation */}
          {selectedVersion && (
            <CheckPanel
              orgId={orgId}
              pid={pid}
              version={selectedVersion}
              canVerify={canVerify}
              canOverride={canOverride}
              canAccept={canAccept}
              checkType="supplement_facts"
            />
          )}
        </div>
      </div>
    </div>
  );
}
